import React, { useState, useRef, useEffect } from "react"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material"
import { useSnackbar, useCompany, useUser } from "src/providers"
import { createGatedCommunity } from "src/services/api/gatedCommunities"
import { getCompanyMembers, type CompanyMember } from "src/services/api/companyMembers"
import { LabeledField } from "./companyInfoComponents"
import CustomAutocomplete from "src/components/CustomAutocomplete"

interface AddGatedCommunityDialogProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
}

export const AddGatedCommunityDialog = ({ open, onClose, onCreated }: AddGatedCommunityDialogProps) => {
  const { snackbarShowMessage } = useSnackbar()
  const user = useUser()
  const { companyId } = useCompany()
  const [name, setName] = useState("")
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [memberOptions, setMemberOptions] = useState<CompanyMember[]>([])
  const [selectedMembers, setSelectedMembers] = useState<CompanyMember[]>([])
  const showError = touched && !name.trim()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    const id = setTimeout(() => inputRef.current?.focus(), 100)
    getCompanyMembers(companyId, { limit: 1000 })    
      .then((r) => setMemberOptions(r.results.filter((m) => m.email !== user.email)))
    return () => clearTimeout(id)
  }, [open])

  const handleClose = () => {
    setName("")
    setTouched(false)
    setSelectedMembers([])
    onClose()
  }

  const handleCreate = () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setSubmitting(true)
    createGatedCommunity({ name: trimmed, memberIds: selectedMembers.map((m) => m.userId) })
      .then(() => {
        snackbarShowMessage("Knowledge Base created", "success")
        handleClose()
        onCreated()
      })
      .catch(() => snackbarShowMessage("Failed to create Knowledge Base", "error"))
      .finally(() => setSubmitting(false))
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: "25px", pt: 4, px: 4 } }}
    >
      <DialogTitle sx={{ fontSize: "32px", fontWeight: 600, px: 0 }}>
        New Knowledge Base
      </DialogTitle>
      <DialogContent sx={{ px: 0, py: 2 }}>
        <Stack spacing={2}>
          <LabeledField
            label="Name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            error={showError}
            helperText={showError ? "Required" : undefined}
            inputRef={inputRef}
          />
          <Typography variant="h6">Add members</Typography>
          <CustomAutocomplete<CompanyMember>
            label="Members"
            multiple
            options={memberOptions}
            value={selectedMembers}
            getOptionLabel={(m) => `${m.firstName} ${m.lastName} (${m.email})`}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            onChange={(_, val) => setSelectedMembers((Array.isArray(val) ? val : []) as CompanyMember[])}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 0, pb: 2, pt: 3, gap: 0.5 }}>
        <Button variant="contained" color="primary" onClick={handleCreate} disabled={!name.trim() || submitting}>
          Add
        </Button>
        <Button variant="outlined" color="secondary" onClick={handleClose}>
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  )
}
